/**
 * Granel Vital — barra de navegación con paneles.
 *
 * Un solo panel abierto a la vez. Los disparadores son <button> con
 * aria-expanded, así que la barra se opera igual con teclado que con puntero:
 * Enter o Espacio abren, Escape cierra y devuelve el foco, Tab fuera cierra.
 *
 * En escritorio abre también al pasar el cursor, con una pequeña espera para
 * que cruzar la barra en diagonal no dispare paneles que nadie pidió.
 */
const HOVER_IN = 120;
const HOVER_OUT = 180;

class GvNav extends HTMLElement {
  connectedCallback() {
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.groups = [...this.querySelectorAll('[data-gv-trigger]')]
      .map((trigger) => ({ trigger, panel: this.querySelector(`#${CSS.escape(trigger.getAttribute('aria-controls'))}`) }))
      .filter((g) => g.panel);

    for (const g of this.groups) {
      g.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.#toggle(g);
      });
      g.trigger.addEventListener('pointerenter', () => this.#hoverOpen(g));
      g.panel.addEventListener('pointerenter', () => window.clearTimeout(this.#closeTimer));
      g.trigger.addEventListener('pointerleave', () => this.#hoverClose());
      g.panel.addEventListener('pointerleave', () => this.#hoverClose());
    }

    // En document: el foco puede estar fuera del componente cuando se abre
    // con puntero, y Escape debe cerrar igual.
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocClick);
    // Al navegar dentro del panel el foco sale del componente: si eso ocurre,
    // el panel debe cerrarse solo.
    this.addEventListener('focusout', this.#onFocusOut);

    this.#mountMobileMenu();
  }

  /**
   * Coloca la versión de móvil dentro del cajón del tema.
   *
   * El cajón lo pinta la sección del encabezado, que se renderiza antes que
   * esta, así que Liquid no puede escribir ahí. Y el tema lo vuelve a pintar
   * después de la carga inicial —hace un primer render ligero y luego lo
   * refresca—, así que no basta con mover el nodo una vez: se perdería.
   *
   * Por eso el original se queda oculto como plantilla y se clona en el hueco
   * cada vez que aparece uno vacío. Un MutationObserver lo cubre pase lo que
   * pase: primer render, refresco del tema o cambio en el editor.
   */
  #mountMobileMenu() {
    const fill = () => {
      const template = document.getElementById('gv-nav-mobile');
      if (!template) return;
      for (const slot of document.querySelectorAll('[data-gv-nav-slot]')) {
        if (slot.childElementCount > 0) continue;
        const copy = template.cloneNode(true);
        copy.removeAttribute('id');
        copy.hidden = false;
        slot.appendChild(copy);
      }
    };

    fill();

    let queued = false;
    this.#observer = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        fill();
      });
    });
    this.#observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocClick);
    document.removeEventListener('keydown', this.#onKeydown);
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    this.#observer?.disconnect();
  }

  /** @type {{trigger: HTMLElement, panel: HTMLElement} | null} */
  #current = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #openTimer = 0;
  #closeTimer = 0;

  #open(group) {
    if (this.#current === group) return;
    this.close();
    group.panel.hidden = false;
    group.trigger.setAttribute('aria-expanded', 'true');
    this.#current = group;
  }

  close() {
    if (!this.#current) return;
    this.#current.panel.hidden = true;
    this.#current.trigger.setAttribute('aria-expanded', 'false');
    this.#current = null;
  }

  #toggle(group) {
    if (this.#current === group) this.close();
    else this.#open(group);
  }

  #hoverOpen(group) {
    if (!this.desktop.matches) return;
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    // Si ya hay un panel abierto el cambio es inmediato: dentro de la barra el
    // usuario ya decidió que quiere ver paneles.
    if (this.#current) this.#open(group);
    else this.#openTimer = window.setTimeout(() => this.#open(group), HOVER_IN);
  }

  #hoverClose() {
    if (!this.desktop.matches) return;
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    this.#closeTimer = window.setTimeout(() => this.close(), HOVER_OUT);
  }

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#current) return;
    const { trigger } = this.#current;
    this.close();
    trigger.focus();
  };

  #onDocClick = (event) => {
    if (!this.#current) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.close();
  };

  #onFocusOut = (event) => {
    if (!this.#current) return;
    const next = event.relatedTarget;
    if (next instanceof Node && this.contains(next)) return;
    this.close();
  };
}

if (!customElements.get('gv-nav')) {
  customElements.define('gv-nav', GvNav);
}
